import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isRestricted, isEmailUnverified } from "@/constants/ageContract";
import {
  readSessionDraft, clearSessionDraft, mergedFormFromSession, isDraftComplete,
  isSessionDraftExpired, draftAvailabilityLabel, AI_RESUME_DISMISS_KEY,
} from "@/lib/aiDraft";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

// Post-Verification-Resume: Ist der Nutzer inzwischen verifiziert und liegt noch ein
// (nicht übernommener, nicht bewusst verworfener) KI-Entwurf in der Sitzung, wird EINMAL
// pro Sitzung ein Hinweis angeboten. Kein Auto-POST, keine Backend-/Auth-Änderung.
export default function AiResumePrompt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const gated = isRestricted(user) || isEmailUnverified(user);
  // Auf Planer-/Formularseiten UND auf /entdecken & /reisen (dort greift die Resume-Karte) nicht anzeigen.
  const suppressed = location.pathname.startsWith("/reise/neu")
    || location.pathname === "/entdecken"
    || location.pathname === "/reisen";
  const [availLabel, setAvailLabel] = useState(null);

  useEffect(() => {
    if (!user || gated || suppressed) { setOpen(false); return; }
    let dismissed = false;
    try { dismissed = sessionStorage.getItem(AI_RESUME_DISMISS_KEY) === "1"; } catch { /* ignore */ }
    if (dismissed) { setOpen(false); return; }
    const s = readSessionDraft();
    setAvailLabel(s ? draftAvailabilityLabel(s) : null);
    setOpen(!!s);
  }, [user, gated, suppressed, location.pathname]);

  const later = () => {
    // Draft bleibt erhalten; nur der Auto-Hinweis wird für diese Sitzung geschlossen.
    try { sessionStorage.setItem(AI_RESUME_DISMISS_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  const resume = () => {
    const s = readSessionDraft();
    const form = mergedFormFromSession(s);
    // Ungültig / unvollständig / abgelaufen -> nicht übernehmen, sicherer Hinweis + zurück zum Planer.
    if (!form || isSessionDraftExpired(s) || !isDraftComplete(form)) {
      toast.error("Dein gespeicherter Reiseentwurf ist unvollständig oder nicht mehr aktuell. Bitte prüfe ihn im KI-Planer.");
      setOpen(false);
      navigate("/reise/neu/ki");
      return;
    }
    // Erfolgreiche Übernahme -> Entwurf konsumieren (kein erneuter Hinweis).
    clearSessionDraft();
    try { sessionStorage.removeItem(AI_RESUME_DISMISS_KEY); } catch { /* ignore */ }
    setOpen(false);
    navigate("/reise/neu/manuell", { state: { initialDraft: form } });
  };

  if (!open) return null;
  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) later(); }}>
      <AlertDialogContent data-testid="ai-resume-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary/15 text-secondary"><Sparkle size={18} weight="fill" /></span>
            Dein Reiseentwurf ist noch da
          </AlertDialogTitle>
          <AlertDialogDescription>
            Du hast deine Verifizierung erfolgreich abgeschlossen. Möchtest du deinen geplanten Reiseentwurf jetzt in das Reiseformular übernehmen?
          </AlertDialogDescription>
        </AlertDialogHeader>
        {availLabel && <p data-testid="ai-resume-avail" className="-mt-1 text-xs text-muted-foreground">{availLabel}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="ai-resume-later" onClick={later}>Später</AlertDialogCancel>
          <AlertDialogAction data-testid="ai-resume-take" onClick={resume} className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
            Reiseentwurf übernehmen<ArrowRight size={18} weight="bold" className="ml-1.5" />
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
